import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  Alert,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import ContactTiles from '../components/shared/ContactTiles';

const CommunityCarePage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        New Life Community Care
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph align="center" sx={{ mb: 4 }}>
        Supporting and empowering our community through various initiatives
      </Typography>

      {/* Link to main website */}
      <Box sx={{ 
        mb: 6, 
        p: 4, 
        bgcolor: 'primary.main', 
        color: 'white', 
        borderRadius: 2,
        textAlign: 'center',
        border: '2px solid',
        borderColor: 'primary.dark'
      }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          🌐 Visit Our Main Website
        </Typography>
        <Typography variant="h6" paragraph sx={{ mb: 3 }}>
          Discover the full range of community care programs, accommodation services, and recovery programs we offer
        </Typography>
        <Button 
          variant="contained"
          size="large"
          href="https://newlifecommunitycare.org/" 
          target="_blank"
          endIcon={<OpenInNew />}
          sx={{ 
            bgcolor: 'white', 
            color: 'primary.main',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: 'grey.100'
            }
          }}
        >
          NewlifeCommunityCare.org
        </Button>
      </Box>

      {/* Mission Statement */}
      <Paper sx={{ p: 4, mb: 6, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="h4" gutterBottom align="center">
          Our Mission
        </Typography>
        <Typography variant="body1" align="center" paragraph>
          New Life Community Care is dedicated to providing comprehensive support services to individuals and families in need,
          fostering a stronger, more resilient community through various programs and initiatives.
        </Typography>
      </Paper>

      {/* Services Grid */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Counseling Services
              </Typography>
              <Typography variant="body1" paragraph>
                Professional counseling services are available to help individuals and families navigate through
                challenging times and build stronger relationships. Our experienced counselors provide emotional support,
                chaplaincy services, and advocacy for those in need.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                href="https://newlifecommunitycare.org/" 
                target="_blank"
                endIcon={<OpenInNew />}
              >
                Learn More
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Youth Programs
              </Typography>
              <Typography variant="body1" paragraph>
                Our youth programs provide a safe and supportive environment for young people to learn, grow, and
                develop essential life skills. We offer youth and parent counseling, prison and chaplaincy work,
                and youth support services.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                href="https://newlifecommunitycare.org/" 
                target="_blank"
                endIcon={<OpenInNew />}
              >
                Learn More
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                New Life Accommodation
              </Typography>
              <Typography variant="body1" paragraph>
                The place to be for shelter and rest, recreation and renewal for people experiencing hardship and homelessness.
                Nestled on 3 acres of bushland, our alcohol & smoke-free unit environment provides fully self-contained
                accommodation in the heart of the beautiful Gold Coast.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                href="https://newlifecommunitycare.org/" 
                target="_blank"
                endIcon={<OpenInNew />}
              >
                Learn More
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                New Life 12 Step Recovery Program
              </Typography>
              <Typography variant="body1" paragraph>
                This new hope awareness program is designed to meet the needs of people who wish to discover more about
                a twelve step self-help growth programme. We encourage each person to care for and love themselves
                in an appropriate manner.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                href="https://newlifecommunitycare.org/" 
                target="_blank"
                endIcon={<OpenInNew />}
              >
                Learn More
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Food Support
              </Typography>
              <Typography variant="body1" paragraph>
                Our food parcel program provides essential groceries to families in need, ensuring they have access to
                nutritious meals throughout the week. We also operate a discount supermarket for members and people
                experiencing hardship.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                href="/food-parcels"
              >
                Food Parcels
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Get Involved Section */}
      <Paper sx={{ p: 4, mt: 6, bgcolor: 'grey.100' }}>
        <Typography variant="h4" gutterBottom align="center">
          Get Involved
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Volunteer
              </Typography>
              <Typography variant="body1" paragraph>
                Join our team of dedicated volunteers and make a difference in your community.
              </Typography>
              <Button variant="outlined" color="primary" href="/volunteer">
                Volunteer Now
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Donate
              </Typography>
              <Typography variant="body1" paragraph>
                Your support helps us continue providing essential services to those in need.
              </Typography>
              <Button variant="outlined" color="primary" href="/donations">
                Make a Donation
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Partner With Us
              </Typography>
              <Typography variant="body1" paragraph>
                We welcome partnerships with organizations and businesses that share our vision.
              </Typography>
              <Button variant="outlined" color="primary" href="/contact">
                Contact Us
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Contact Information */}
      <Box sx={{ mt: 8 }}>
        <ContactTiles />
      </Box>
    </Container>
  );
};

export default CommunityCarePage; 